// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { linkArrowStyle, mergeWithCss } from "@werner/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface MiniSignInProps {
    extendedStyles?: MiniSignInStyles;
}

type Props = MiniSignInProps;

interface MiniSignInStyles {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    createAccountLink?: LinkPresentationProps;
}

const miniSignInStyles: MiniSignInStyles = {
    container: {
        css: css`
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            flex-direction: column;
            max-width: 400px;
            padding: 45px 50px 16px;
        `,
    },
    title: {
        size: 28,
        weight: 500,
        css: css`
            letter-spacing: 0;
            line-height: 32px;
            text-align: center;
            margin-bottom: 34px;
            width: 100%;
        `,
    },
    createAccountLink: {
        ...mergeWithCss(linkArrowStyle, {
            css: css`
                margin: 20px auto 0;
            `,
        }),
    },
};

const MiniSignIn: FC<Props> = ({ extendedStyles }) => {
    const { allowCreateAccount, createAccountUrl } = useAppSelector(state => {
        const { search } = getLocation(state);
        const accountSettings = getSettingsCollection(state).accountSettings;
        const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
        return {
            allowCreateAccount: accountSettings.allowCreateAccount,
            createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
        };
    });
    const history = useHistory();
    const [styles] = useState(() => mergeWithCss(miniSignInStyles, extendedStyles));

    const handleCreateNewAccount = () => {
        if (!createAccountUrl) {
            return;
        }
        hideAll();
        history.push(createAccountUrl);
    };

    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.title}>{translate("Welcome")!}</Typography>
            {createWidgetElement("SignIn/SignInExistingAccount", {
                fields: {
                    isMiniSignIn: true,
                    fullWidthButton: true,
                },
            })}
            {allowCreateAccount && createAccountUrl && (
                <Link
                    onClick={handleCreateNewAccount}
                    {...styles.createAccountLink}
                    aria-label="Register for an Account"
                >
                    {translate("Register for an Account")}
                </Link>
            )}
        </StyledWrapper>
    );
};

export default MiniSignIn;
